import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import sidebarLogo from '@/assets/sidebar-logo.png'
import { useNavigate } from 'react-router-dom'
import { Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { authApi } from '@/api/endpoints'
import { toast } from '@/lib/toast'
import { Footer } from '@/components/layout/Footer'
import { cn } from '@/lib/utils'
import { useDirectionStore } from '@/store/directionStore'

type View = 'login' | 'forgot' | 'reset'

function PasswordField({
  id, value, onChange, placeholder, autoComplete, autoFocus,
}: {
  id: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  autoComplete?: string
  autoFocus?: boolean
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <Input
        id={id}
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
        title={show ? 'Hide password' : 'Show password'}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
}

export function LoginPage() {
  const { t } = useTranslation('auth')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const d = useDirectionStore((s) => s.direction)
  const [isLoading, setIsLoading] = useState(false)
  const [view, setView] = useState<View>('login')

  // Login form
  const [loginData, setLoginData] = useState({ email: '', password: '' })

  // Forgot password form
  const [forgotEmail, setForgotEmail] = useState('')

  // Reset password form
  const [resetData, setResetData] = useState({ otp: '', newPassword: '', confirmPassword: '' })

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsLoading(true)

    try {
      const response = await authApi.login({
        email: loginData.email.trim().toLowerCase(),
        password: loginData.password,
        device_type: 'web',
      })

      login(response.user, response.tokens.access, response.tokens.refresh)

      // Sync dashboard preferences from user's stored profile
      // Use setState directly to avoid redundant PATCH back to server
      const uiState = useUIStore.getState()
      if (response.user.language && response.user.language !== i18next.language) {
        i18next.changeLanguage(response.user.language)
        document.documentElement.lang = response.user.language
      }
      // Always update UI store language so the toggle reflects the correct state
      if (response.user.language) {
        useUIStore.setState({ language: response.user.language })
      }
      if (response.user.theme && response.user.theme !== uiState.theme) {
        // Apply CSS class manually, then set store — avoids the PATCH in setTheme()
        const root = window.document.documentElement
        root.classList.remove('light', 'dark')
        if (response.user.theme === 'system') {
          root.classList.add(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        } else {
          root.classList.add(response.user.theme)
        }
        useUIStore.setState({ theme: response.user.theme })
      }

      toast.success(t('messages.welcomeBack'), t('messages.loggedInAs', { name: response.user.username }))
      navigate('/')
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { detail?: string } } }
      const errorMessage = apiError.response?.data?.detail || t('messages.invalidCredentials')
      toast.error(t('messages.loginFailed'), errorMessage, { id: 'login-error' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await authApi.requestPasswordReset(forgotEmail)
      toast.success(t('messages.codeSent'), t('messages.resetCodeSent'))
      setView('reset')
    } catch {
      toast.error(tc('error.title'), t('messages.sendResetFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (resetData.newPassword !== resetData.confirmPassword) {
      toast.error(tc('error.title'), t('messages.passwordMismatch'))
      return
    }

    setIsLoading(true)

    try {
      await authApi.confirmPasswordReset(forgotEmail, resetData.otp, resetData.newPassword)
      toast.success(t('messages.passwordReset'), t('messages.passwordResetSuccess'))
      setView('login')
      setResetData({ otp: '', newPassword: '', confirmPassword: '' })
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } }
      const errorMessage = apiError.response?.data?.error || t('messages.resetFailedMessage')
      toast.error(t('messages.resetFailed'), errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn(
      'flex min-h-screen flex-col items-center justify-center p-4',
      // current: token-based gradient (verbatim)
      d === 'current' && 'bg-gradient-to-br from-primary/5 via-background to-muted/30',
      // command: flat bg-background (graphite via tokens — dark-first)
      d === 'command' && 'bg-background',
      // saas: near-white / engineered dark — bg-background suffices
      d === 'saas' && 'bg-background',
      // expressive: gradient canvas utility
      d === 'expressive' && 'bg-canvas',
    )}>

      {/* Elevated card */}
      <div className={cn(
        'w-full max-w-sm border bg-background',
        // current: verbatim rounded-2xl + shadow + ring
        d === 'current' && 'rounded-2xl border-border/60 shadow-elevation-3 ring-1 ring-border/30',
        // command: sharp rounded-sm, border-border
        d === 'command' && 'rounded-sm border-border shadow-md',
        // saas: hairline card, minimal radius
        d === 'saas' && 'rounded-md border-border shadow-sm',
        // expressive: glass card, large radius, elevated shadow
        d === 'expressive' && 'bg-glass rounded-2xl border-border/60 shadow-elevation-2',
      )}>

        {/* Card header — keyed by view for entrance animation */}
        <div className="flex flex-col items-center px-8 pt-8 pb-6 border-b border-border/40">
          <img
            src={sidebarLogo}
            alt="DeterCam"
            className="h-10 w-auto object-contain mb-5"
          />
          <div key={`header-${view}`} className="flex flex-col items-center animate-fade-up">
            {view === 'login' && (
              <>
                <h1 className={cn(
                  'text-subheading',
                  d === 'command' && 'font-mono uppercase tracking-wider',
                  d === 'expressive' && 'font-display text-2xl',
                )}>{t('login.title')}</h1>
                <p className={cn('mt-1 text-body text-muted-foreground', d === 'saas' && 'text-[13px]')}>{t('login.subtitle')}</p>
              </>
            )}
            {view === 'forgot' && (
              <>
                <h1 className={cn(
                  'text-subheading',
                  d === 'command' && 'font-mono uppercase tracking-wider',
                  d === 'expressive' && 'font-display text-2xl',
                )}>{t('forgot.title')}</h1>
                <p className={cn('mt-1 text-body text-muted-foreground text-center', d === 'saas' && 'text-[13px]')}>
                  {t('forgot.subtitle')}
                </p>
              </>
            )}
            {view === 'reset' && (
              <>
                <h1 className={cn(
                  'text-subheading',
                  d === 'command' && 'font-mono uppercase tracking-wider',
                  d === 'expressive' && 'font-display text-2xl',
                )}>{t('reset.title')}</h1>
                <p className={cn('mt-1 text-body text-muted-foreground text-center', d === 'saas' && 'text-[13px]')}>
                  {t('reset.subtitlePlain', { email: forgotEmail })}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Card body — keyed by view for entrance animation */}
        <div key={`body-${view}`} className={cn('px-8 py-6 animate-fade-up', d === 'saas' && 'text-[13px]')}>

          {/* Login form */}
          {view === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="email" className="text-body font-medium">
                  {t('login.email')}
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-body font-medium">
                    {t('login.password')}
                  </label>
                  <button
                    type="button"
                    onClick={() => setView('forgot')}
                    className="text-label text-muted-foreground hover:text-primary transition-colors underline underline-offset-4 decoration-muted-foreground/40 hover:decoration-primary"
                  >
                    {t('login.forgotPassword')}
                  </button>
                </div>
                <PasswordField
                  id="password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  autoComplete="current-password"
                />
              </div>

              <Button type="submit" className="w-full mt-2" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('login.signingIn')}
                  </>
                ) : (
                  t('login.signIn')
                )}
              </Button>
            </form>
          )}

          {/* Forgot password form */}
          {view === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="forgot-email" className="text-body font-medium">
                  {t('forgot.emailAddress')}
                </label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('forgot.sendingCode')}
                  </>
                ) : (
                  t('forgot.sendResetCode')
                )}
              </Button>

              <button
                type="button"
                onClick={() => setView('login')}
                className="flex w-full items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {t('forgot.backToSignIn')}
              </button>
            </form>
          )}

          {/* Reset password form */}
          {view === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="otp" className="text-body font-medium">
                  {t('reset.resetCode')}
                </label>
                <Input
                  id="otp"
                  type="text"
                  placeholder={t('reset.codePlaceholder')}
                  value={resetData.otp}
                  onChange={(e) => setResetData({ ...resetData, otp: e.target.value })}
                  required
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="new-password" className="text-body font-medium">
                  {t('reset.newPassword')}
                </label>
                <PasswordField
                  id="new-password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={resetData.newPassword}
                  onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                  autoComplete="new-password"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="confirm-password" className="text-body font-medium">
                  {t('reset.confirmPassword')}
                </label>
                <PasswordField
                  id="confirm-password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={resetData.confirmPassword}
                  onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                  autoComplete="new-password"
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('reset.resettingPassword')}
                  </>
                ) : (
                  t('reset.resetPassword')
                )}
              </Button>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isLoading}
                  className="hover:text-foreground transition-colors underline-offset-4 hover:underline disabled:opacity-50"
                >
                  {t('reset.resendCode')}
                </button>
                <button
                  type="button"
                  onClick={() => setView('login')}
                  className="flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t('forgot.backToSignIn')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="mt-6">
        <Footer />
      </div>
    </div>
  )
}
